/**
 * Legacy API client — frozen for v0.7.
 *
 * This client talks to the pre-v2 surface (`/apps/:name/*`, `/admin/*`, `/hooks/*`,
 * `/tunnel/*`). It is used ONLY by preserved-on-legacy commands:
 *   remove, rollback, connect, disconnect, builds, pull, hooks, ssh, dev,
 *   doctor, usage, support, feedback, and the deploy build pipeline (Task 17).
 *
 * New v0.7 commands MUST use src/api/v2-client.ts instead.
 * The zero-legacy grep (Task 44) excludes this file by path.
 */

import * as net from 'net';
import WebSocket from 'ws';
import { isVerbose } from '../utils.js';
import { SSEParser, type SSEEvent } from '../sse-parser.js';
import { translateFetchError } from './fetch-errors.js';
import { CLI_VERSION } from '../cli-version.js';

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ── Response types ──────────────────────────────────────────────

export interface BuildResponseEntity {
  id: string;
  name: string;
  namespace?: string;
}

export type BuildResponse =
  | { kind: 'queued';   trace_id: string; entity: BuildResponseEntity; poll_url: string; status: 'queued' }
  | { kind: 'attached'; trace_id: string; entity: BuildResponseEntity; poll_url: string; status: string; message?: string };

export interface DeployResult {
  status: string;
  url: string;
  services: { name: string; image: string; ready: boolean }[];
}

export interface AppEntry {
  name: string;
  domain: string;
  services: { name: string; image: string }[];
  deployed_at: string;
  deployed_by: string;
}

export interface PodInfo {
  name: string;
  status: string;
  ready: boolean;
  restarts: number;
  age: string | null;
  startTime: string | null;
  lastTerminationReason: string | null;
  cpu: { used: string | null; requested: string | null };
  memory: { used: string | null; requested: string | null };
}

export interface ServiceInfo {
  name: string;
  type: string;
  ports: string;
}

export interface IngressInfo {
  host: string;
  ip: string;
}

export interface LastDeploySummary {
  status: string;
  sha: string | null;
  message: string | null;
  triggeredBy: string;
  source: string;
  durationSeconds: number | null;
  finishedAt: string | null;
}

export interface AppStatus {
  name: string;
  domain: string;
  pods: PodInfo[];
  services: ServiceInfo[];
  ingress: IngressInfo | null;
  lastDeploy: LastDeploySummary | null;
}

export interface LogLine {
  pod: string;
  service: string | null;
  ts: string;
  line: string;
}

export interface TruncatedInfo {
  pod: string;
  started: string;
  requested: string;
}

export interface LogsResult {
  lines: LogLine[];
  truncated: TruncatedInfo[];
}

/**
 * The legacy v1 error envelope. The v2 envelope wraps everything in a
 * nested `error` object; `parseLegacyOrV2ErrorBody` accepts either shape.
 */
export interface ApiErrorBody {
  error: string;
  code: string;
}

export interface StreamLogsOptions {
  service?: string;
  tail?: number;
  since?: string;
  signal?: AbortSignal;
}

export interface PollDeployOptions {
  /** Called on every poll with the full trace response. Renderers should
   *  diff `trace.steps` to update their display. */
  onPoll?: (trace: any) => void;
  /** Default 2000 ms. */
  pollIntervalMs?: number;
  /** Default 10 min. */
  maxDurationMs?: number;
  /** Optional external cancellation. */
  signal?: AbortSignal;
}

export type { SSEEvent };

// ── Error class ─────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ── Client ──────────────────────────────────────────────────────

/**
 * Parse the response body of a non-2xx response into an `ApiError`. Accepts
 * both the legacy v1 envelope (`{ error: string, code: string }`) and the
 * v2 envelope (`{ error: { code, message, details, request_id } }`). The
 * API's per-endpoint behaviour determined which shape was emitted, but the
 * org-context middleware sits in front of every endpoint and emits the v2
 * shape — so any legacy endpoint behind that middleware can return v2.
 *
 * Without this, multi-org users hit `ORG_HEADER_REQUIRED` from the
 * middleware (v2 envelope) and saw `API error (undefined): [object Object]`
 * because the legacy parser tried to read `body.error` as a string.
 */
export function parseLegacyOrV2ErrorBody(status: number, statusText: string, body: unknown): ApiError {
  if (body && typeof body === 'object') {
    const b = body as any;
    // v2 shape — `error` is a nested object with code + message.
    if (b.error && typeof b.error === 'object' && typeof b.error.code === 'string') {
      const message = typeof b.error.message === 'string' ? b.error.message : statusText;
      return new ApiError(status, b.error.code, message);
    }
    // v1 shape — string `error` message at the top level. `code` may be
    // either a string symbol (`"VALIDATION_FAILED"`) or a numeric Cloud
    // Build status (e.g. `2` for build failures); we coerce to string so
    // downstream code can compare uniformly.
    if (typeof b.error === 'string') {
      const code = typeof b.code === 'string' ? b.code
                 : typeof b.code === 'number' ? String(b.code)
                 : 'UNKNOWN';
      return new ApiError(status, code, b.error);
    }
  }
  return new ApiError(status, 'UNKNOWN', statusText);
}

export class CloudGridApiClient {
  private baseUrl: string;
  private jwt: string;
  private orgSlug: string | null;

  constructor(
    apiUrl: string,
    jwt: string,
    orgSlug: string | null = null,
  ) {
    // Strip trailing slash
    this.baseUrl = apiUrl.replace(/\/+$/, '');
    this.jwt = jwt;
    this.orgSlug = orgSlug;
  }

  // ── HTTP helpers ────────────────────────────────────────────

  /**
   * Build the request headers, always including `Authorization` and —
   * when set — `X-CloudGrid-Org`. The org header lets the API's
   * org-context middleware pick the active org without having to make
   * the user pass `--org` on every command. Without it, users in 2+
   * orgs are blocked with `ORG_HEADER_REQUIRED`.
   */
  private buildHeaders(extra?: Record<string, string>): Record<string, string> {
    const h: Record<string, string> = {
      'authorization': `Bearer ${this.jwt}`,
      ...extra,
    };
    // BL588 Phase E PR-E3 — emit both the legacy `x-cloudgrid-org` and the new
    // canonical `x-cloudgrid-grid` header (same value). The API accepts either
    // (Grid wins when both are present); Phase F drops the org name.
    if (this.orgSlug) {
      h['x-cloudgrid-org'] = this.orgSlug;
      h['x-cloudgrid-grid'] = this.orgSlug;
    }
    return h;
  }

  public async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const headers = this.buildHeaders(
      body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    );

    if (isVerbose()) {
      console.error(`  [verbose] ${method} ${url}`);
    }

    let res: Response;
    try {
      res = await fetch(url, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: AbortSignal.timeout(15_000),
      });
    } catch (err) {
      throw translateFetchError(err);
    }

    if (isVerbose()) {
      console.error(`  [verbose] ${res.status} ${res.statusText}`);
    }

    if (!res.ok) {
      let parsed: unknown;
      try {
        parsed = await res.json();
      } catch {
        parsed = null;
      }
      throw parseLegacyOrV2ErrorBody(res.status, res.statusText, parsed);
    }

    // 204 No Content
    if (res.status === 204) return undefined as T;

    return (await res.json()) as T;
  }

  // ── Public API methods ──────────────────────────────────────

  async build(
    name: string,
    services: Record<string, string>,
    cloudgridYaml: string,
    tag?: string,
  ): Promise<BuildResponse> {
    const url = `${this.baseUrl}/apps/${name}/build`;
    const headers = this.buildHeaders({
      'Content-Type': 'application/json',
      'x-cloudgrid-cli-version': CLI_VERSION,
    });
    let res: Response;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ cloudgrid_yaml: cloudgridYaml, services, tag }),
        signal: AbortSignal.timeout(15_000),
      });
    } catch (err) {
      throw translateFetchError(err);
    }
    let body: any = null;
    try { body = await res.json(); } catch { /* no json body */ }

    if (res.status === 202) {
      return {
        kind: 'queued',
        trace_id: body?.trace_id,
        entity: body?.entity ?? { id: '', name },
        poll_url: body?.poll_url ?? `/deploys/${body?.trace_id}`,
        status: 'queued',
      };
    }
    if (res.status === 409) {
      return {
        kind: 'attached',
        trace_id: body?.trace_id,
        entity: body?.entity ?? { id: '', name },
        poll_url: body?.poll_url ?? `/deploys/${body?.trace_id}`,
        status: body?.status ?? 'building',
        message: body?.message,
      };
    }
    throw parseLegacyOrV2ErrorBody(res.status, res.statusText, body);
  }

  async pollDeploy(traceId: string, opts: PollDeployOptions = {}): Promise<any> {
    const pollIntervalMs = opts.pollIntervalMs ?? 2_000;
    const maxDurationMs  = opts.maxDurationMs  ?? 10 * 60_000;
    const startMs = Date.now();
    let consecutiveFailures = 0;

    while (true) {
      if (opts.signal?.aborted) {
        throw new ApiError(0, 'POLL_ABORTED', `Poll aborted for trace ${traceId}`);
      }
      if (Date.now() - startMs > maxDurationMs) {
        throw new ApiError(
          0,
          'POLL_TIMEOUT',
          `Deploy trace ${traceId} did not reach terminal state within ${maxDurationMs / 60_000} min`,
        );
      }

      let trace: any;
      try {
        trace = await this.request<any>('GET', `/deploys/${traceId}`);
        consecutiveFailures = 0;
      } catch (err) {
        consecutiveFailures++;
        if (consecutiveFailures >= 3) {
          throw new ApiError(0, 'POLL_NETWORK_LOST', `Lost connection polling trace ${traceId}`);
        }
        await sleep(pollIntervalMs);
        continue;
      }

      opts.onPoll?.(trace);

      if (trace.status === 'success' || trace.status === 'failed') {
        return trace;
      }

      await sleep(pollIntervalMs);
    }
  }

  async deploy(
    name: string,
    images: Record<string, string>,
    cloudgridYaml: string,
  ): Promise<DeployResult> {
    return this.request<DeployResult>('POST', `/apps/${name}/deploy`, {
      images,
      cloudgrid_yaml: cloudgridYaml,
    });
  }

  async list(): Promise<{ apps: AppEntry[] }> {
    return this.request<{ apps: AppEntry[] }>('GET', '/apps');
  }

  async status(name: string): Promise<AppStatus> {
    return this.request<AppStatus>('GET', `/apps/${name}`);
  }

  async logs(
    name: string,
    opts?: { service?: string; tail?: number; since?: string },
  ): Promise<LogsResult> {
    const params = new URLSearchParams();
    if (opts?.service) params.set('service', opts.service);
    if (opts?.tail) params.set('tail', String(opts.tail));
    if (opts?.since) params.set('since', opts.since);
    const qs = params.toString();
    const path = `/apps/${name}/logs${qs ? `?${qs}` : ''}`;
    return this.request<LogsResult>('GET', path);
  }

  /**
   * Async generator that yields SSE events from /logs/stream.
   * Caller is responsible for reconnect logic.
   * Pass an AbortSignal to cancel.
   */
  async *streamLogs(name: string, opts: StreamLogsOptions = {}): AsyncGenerator<SSEEvent> {
    const params = new URLSearchParams();
    if (opts.service) params.set('service', opts.service);
    if (opts.tail !== undefined) params.set('tail', String(opts.tail));
    if (opts.since) params.set('since', opts.since);
    const qs = params.toString();
    const url = `${this.baseUrl}/apps/${name}/logs/stream${qs ? `?${qs}` : ''}`;

    let res: Response;
    try {
      res = await fetch(url, {
        method: 'GET',
        headers: this.buildHeaders({ accept: 'text/event-stream' }),
        signal: opts.signal,
      });
    } catch (err) {
      throw translateFetchError(err);
    }

    if (!res.ok) {
      let parsed: unknown;
      try {
        parsed = await res.json();
      } catch {
        parsed = null;
      }
      throw parseLegacyOrV2ErrorBody(res.status, res.statusText, parsed);
    }
    if (!res.body) throw new Error('No response body');

    const parser = new SSEParser();
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    const queue: SSEEvent[] = [];

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      parser.feed(decoder.decode(value, { stream: true }), (ev) => queue.push(ev));
      while (queue.length > 0) yield queue.shift()!;
    }
    // Flush any remaining buffered event
    parser.feed('\n\n', (ev) => queue.push(ev));
    while (queue.length > 0) yield queue.shift()!;
  }

  async remove(name: string): Promise<void> {
    await this.request<{ status: string }>('DELETE', `/apps/${name}`);
  }

  async listRepos(): Promise<{ repos: { full_name: string; default_branch: string }[] }> {
    return this.request('GET', '/github/repos');
  }

  async connect(name: string, repo: string, branch: string): Promise<any> {
    return this.request('POST', `/apps/${name}/connect`, { repo, branch });
  }

  async disconnect(name: string): Promise<any> {
    return this.request('DELETE', `/apps/${name}/connect`);
  }

  async listBuilds(name: string): Promise<any> {
    return this.request('GET', `/apps/${name}/builds`);
  }

  async listSecrets(name: string): Promise<any> {
    return this.request('GET', `/apps/${name}/secrets`);
  }

  async setSecrets(name: string, values: Record<string, string>): Promise<any> {
    return this.request('POST', `/apps/${name}/secrets`, { values });
  }

  async getSecret(name: string, key: string): Promise<any> {
    return this.request('GET', `/apps/${name}/secrets/${encodeURIComponent(key)}`);
  }

  async removeSecret(name: string, key: string): Promise<any> {
    return this.request('DELETE', `/apps/${name}/secrets/${encodeURIComponent(key)}`);
  }

  async listEnv(name: string): Promise<any> {
    return this.request('GET', `/apps/${name}/env`);
  }

  async setEnv(name: string, values: Record<string, string>): Promise<any> {
    return this.request('POST', `/apps/${name}/env`, { values });
  }

  async removeEnv(name: string, key: string): Promise<any> {
    return this.request('DELETE', `/apps/${name}/env/${encodeURIComponent(key)}`);
  }

  async listEvents(name: string): Promise<any> {
    return this.request('GET', `/apps/${name}/events`);
  }

  async getEntityUsage(orgId: string, entityId: string): Promise<any> {
    return this.request('GET', `/api/v2/orgs/${orgId}/entities/${entityId}/usage`);
  }

  async rollback(name: string): Promise<DeployResult> {
    return this.request<DeployResult>('POST', `/apps/${name}/rollback`);
  }

  async getSource(name: string): Promise<{ services: Record<string, string>; domain?: string }> {
    return this.request('GET', `/apps/${name}/source`);
  }

  async reportError(data: {
    type?: string;
    category?: string;
    app?: string | null;
    message: string;
    stack?: string | null;
    context?: Record<string, unknown> | null;
    cli_version?: string;
    node_version?: string;
    platform?: string;
    trace_id?: string;
    failed_step?: string;
    http_status?: number;
  }): Promise<any> {
    return this.request('POST', '/errors', data);
  }

  async listDeployErrors(opts: { severity?: string; since?: string; limit?: number } = {}): Promise<any[]> {
    const qs = new URLSearchParams({ source: 'server-deploy' });
    if (opts.severity) qs.set('severity', opts.severity);
    if (opts.since)    qs.set('since', opts.since);
    if (opts.limit)    qs.set('limit', String(opts.limit));
    const result = await this.request<{ errors: any[] }>('GET', `/errors?${qs.toString()}`);
    return result.errors ?? [];
  }

  async submitFeedback(data: {
    app?: string | null;
    message: string;
    context?: Record<string, unknown> | null;
    cli_version?: string;
    node_version?: string;
    platform?: string;
  }): Promise<any> {
    return this.request('POST', '/feedback', data);
  }

  async addHook(data: { trigger: string; url: string; app: string | null }): Promise<any> {
    return this.request('POST', '/hooks', data);
  }

  async listHooks(app?: string): Promise<any> {
    const qs = app ? `?app=${encodeURIComponent(app)}` : '';
    return this.request('GET', `/hooks${qs}`);
  }

  async removeHook(id: string): Promise<any> {
    return this.request('DELETE', `/hooks/${encodeURIComponent(id)}`);
  }

  async listDeliveries(limit = 20): Promise<any> {
    return this.request('GET', `/hooks/deliveries?limit=${limit}`);
  }

  // ── WebSocket tunnel ────────────────────────────────────────

  /**
   * @deprecated The canonical `openTunnel` now lives on {@link IV2Client}
   * (`packages/cli/src/api/v2-client.ts`). Per decision 015 the dev runner
   * (and any future tunnel consumer) should resolve a v2 client and call
   * `client.openTunnel(...)`. This legacy implementation is kept only so
   * the legacy client surface stays compilable; new code MUST NOT call it.
   *
   * Opens a local TCP server on `localPort`. For each incoming connection,
   * opens a WebSocket to the API tunnel endpoint and bridges data
   * bidirectionally between the local TCP socket and the WebSocket.
   *
   * Returns the TCP server so the caller can close it.
   */
  openTunnel(service: string, localPort: number): Promise<net.Server> {
    return new Promise((resolve, reject) => {
      const server = net.createServer((tcpSocket) => {
        // Build WebSocket URL: wss for https, ws for http
        const wsProto = this.baseUrl.startsWith('https') ? 'wss' : 'ws';
        const host = this.baseUrl.replace(/^https?:\/\//, '');
        const wsUrl = `${wsProto}://${host}/tunnel/${service}`;

        const wsHeaders = this.buildHeaders();
        const ws = new WebSocket(wsUrl, { headers: wsHeaders });
        ws.binaryType = 'nodebuffer';

        ws.on('open', () => {
          // Bridge TCP → WebSocket
          tcpSocket.on('data', (data) => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(data);
            }
          });

          // Bridge WebSocket → TCP
          ws.on('message', (data: Buffer) => {
            if (!tcpSocket.destroyed) {
              tcpSocket.write(data);
            }
          });
        });

        ws.on('close', () => {
          if (!tcpSocket.destroyed) tcpSocket.destroy();
        });

        ws.on('error', () => {
          if (!tcpSocket.destroyed) tcpSocket.destroy();
        });

        tcpSocket.on('close', () => {
          if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
            ws.close();
          }
        });

        tcpSocket.on('error', () => {
          if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
            ws.close();
          }
        });
      });

      server.on('error', reject);

      server.listen(localPort, '127.0.0.1', () => {
        server.removeListener('error', reject);
        server.on('error', (err) => {
          console.error(`Tunnel server error: ${err.message}`);
        });
        resolve(server);
      });
    });
  }
}
